import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Modal,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { showError } from '../../helpers';

const defaultForm = {
  id: undefined,
  name: '',
  enabled: true,
  match_target: 'message',
  match_type: 'contains',
  pattern: '',
  mapped_code: '',
  mapped_message: '',
  priority: 100,
  remark: '',
};

const matchTargetOptions = (t) => [
  {
    key: 'upstream_code',
    text: t('error_mapping.target.upstream_code'),
    value: 'upstream_code',
  },
  { key: 'message', text: t('error_mapping.target.message'), value: 'message' },
];

const matchTypeOptions = (t) => [
  { key: 'exact', text: t('error_mapping.match_type.exact'), value: 'exact' },
  {
    key: 'contains',
    text: t('error_mapping.match_type.contains'),
    value: 'contains',
  },
  {
    key: 'prefix',
    text: t('error_mapping.match_type.prefix'),
    value: 'prefix',
  },
  { key: 'regex', text: t('error_mapping.match_type.regex'), value: 'regex' },
];

const EditRuleModal = ({ open, loading, rule, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (open) {
      setForm(rule ? { ...defaultForm, ...rule } : defaultForm);
    }
  }, [open, rule]);

  const handleChange = (e, { name, value, checked, type }) => {
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showError(t('error_mapping.validation.name_required'));
      return;
    }
    if (!form.pattern.trim()) {
      showError(t('error_mapping.validation.pattern_required'));
      return;
    }
    if (!form.mapped_code.trim()) {
      showError(t('error_mapping.validation.mapped_code_required'));
      return;
    }
    await onSave({
      ...form,
      priority: Number(form.priority) || 0,
      name: form.name.trim(),
      pattern: form.pattern.trim(),
      mapped_code: form.mapped_code.trim(),
      mapped_message: String(form.mapped_message || '').trim(),
      remark: String(form.remark || '').trim(),
    });
  };

  return (
    <Modal open={open} onClose={onClose} size='large'>
      <Modal.Header>
        {rule
          ? t('error_mapping.modal.edit_title')
          : t('error_mapping.modal.create_title')}
      </Modal.Header>
      <Modal.Content scrolling>
        <Form>
          <Header as='h4' style={{ marginBottom: '6px' }}>
            {t('error_mapping.sections.match', { defaultValue: '匹配条件' })}
          </Header>
          <div style={{ color: '#777', fontSize: '12px', marginBottom: '14px' }}>
            {t('error_mapping.sections.match_hint', {
              defaultValue: '定义什么情况下命中当前规则。',
            })}
          </div>
          <Form.Input
            label={t('error_mapping.form.name')}
            name='name'
            value={form.name}
            onChange={handleChange}
          />
          <Grid stackable columns={2}>
            <Grid.Row>
              <Grid.Column>
                <Form.Select
                  label={t('error_mapping.form.match_target')}
                  name='match_target'
                  options={matchTargetOptions(t)}
                  value={form.match_target}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Select
                  label={t('error_mapping.form.match_type')}
                  name='match_type'
                  options={matchTypeOptions(t)}
                  value={form.match_type}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Form.TextArea
            label={t('error_mapping.form.pattern')}
            name='pattern'
            value={form.pattern}
            onChange={handleChange}
            rows={3}
          />
          <Divider />
          <Header as='h4' style={{ marginBottom: '6px' }}>
            {t('error_mapping.sections.mapping', { defaultValue: '映射结果' })}
          </Header>
          <div style={{ color: '#777', fontSize: '12px', marginBottom: '14px' }}>
            {t('error_mapping.sections.mapping_hint', {
              defaultValue: '定义命中后的内部错误码、展示消息和优先级。',
            })}
          </div>
          <Grid stackable columns={2}>
            <Grid.Row>
              <Grid.Column>
                <Form.Input
                  label={t('error_mapping.form.mapped_code')}
                  name='mapped_code'
                  value={form.mapped_code}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Input
                  label={t('error_mapping.form.priority')}
                  name='priority'
                  type='number'
                  value={form.priority}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Form.TextArea
            label={t('error_mapping.form.mapped_message')}
            name='mapped_message'
            value={form.mapped_message}
            onChange={handleChange}
            rows={3}
          />
          <Form.TextArea
            label={t('error_mapping.form.remark')}
            name='remark'
            value={form.remark}
            onChange={handleChange}
            rows={2}
          />
          <Form.Checkbox
            label={t('error_mapping.form.enabled')}
            name='enabled'
            checked={form.enabled}
            onChange={handleChange}
            style={{ marginTop: '6px' }}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('error_mapping.buttons.cancel')}</Button>
        <Button primary loading={loading} onClick={handleSubmit}>
          {t('error_mapping.buttons.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditRuleModal;
